// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/components/VerbaDeSellInCell/VerbaDeSellInCell.tsx

import classNames from 'classnames';
import { useRef } from 'react';
import type { ChangeEvent } from 'react';
import { CurrencyInput } from '../../../../../../../components/CurrencyInput';
import { InputAddon } from '../../../../../../../components/InputAddon';
import { InputGroup } from '../../../../../../../components/InputGroup';
import { PLACEHOLDER_DA_VERBA, TITULO_DO_BOTAO_DE_UNIDADE } from '../../verbaDeSellIn';
import type { CampoDaVerba, UnidadeDaVerba } from '../../verbaDeSellIn';
import styles from './VerbaDeSellInCell.module.scss';

export interface IVerbaDeSellInCell {
  /** O estado já decidido pelo módulo puro. Esta célula não decide nada: ela desenha. */
  campo: CampoDaVerba;
  unidade: UnidadeDaVerba;
  onChangeUnidade: (unidade: UnidadeDaVerba) => void;
  /** O número digitado, NA UNIDADE CORRENTE. Quem sabe converter é quem tem o preço de concorrência. */
  onChangeValor: (valor: number | null) => void;
  onBlur?: () => void;
}

const SIMBOLO_DA_UNIDADE: Record<UnidadeDaVerba, string> = {
  REAIS: 'R$',
  PONTOS: 'p.p.',
};

/** Reais com duas decimais, pontos de margem com uma. É o protótipo. */
const DECIMAIS_DA_UNIDADE: Record<UnidadeDaVerba, number> = {
  REAIS: 2,
  PONTOS: 1,
};

export const VerbaDeSellInCell = ({ campo, unidade, onChangeUnidade, onChangeValor, onBlur }: IVerbaDeSellInCell) => {
  /**
   * O sinal de menos que ainda não tem dígito para carregá-lo.
   *
   * O campo é controlado pelo NÚMERO que o módulo puro devolve, e menos-zero não é número
   * negativo: a primeira tecla de `-0,10` chega como `-0`, vira campo vazio, e o sinal
   * desapareceria antes do primeiro dígito. Guardá-lo aqui faz o dígito seguinte sair negativo e
   * chegar à régua de recusa, que é quem decide.
   *
   * É referência e não estado porque enquanto vale o campo está vazio de qualquer jeito. E é
   * descartado no dígito seguinte, quando o sinal já vive no próprio número.
   */
  const sinalPendente = useRef(false);

  const aoDigitar = (evento: ChangeEvent<HTMLInputElement>, _mascara: string, valor: number | null) => {
    const temDigito = valor !== null && valor !== 0;
    const negativar = temDigito && sinalPendente.current;

    sinalPendente.current = !temDigito && evento.target.value.includes('-');

    onChangeValor(negativar && valor !== null ? -Math.abs(valor) : valor);
  };

  const aoSairDoCampo = () => {
    sinalPendente.current = false;
    onBlur?.();
  };

  // Linha que não carrega o contrato da verba não ganha célula nenhuma. Desenhar um campo vazio ali
  // afirmaria que não há verba, quando o que não há é o campo na resposta.
  if (campo.estado === 'SEM_CONTRATO') return null;

  const valorNaUnidade = unidade === 'REAIS' ? campo.valor : campo.pontosRecompostos;
  const simbolo = SIMBOLO_DA_UNIDADE[unidade];

  return (
    <div className={styles['verba-cell']}>
      <InputGroup className={classNames(styles['verba-grupo'], styles[`moldura-${campo.moldura.toLowerCase()}`])}>
        <InputAddon>
          <span className={styles['verba-simbolo']}>{simbolo}</span>
        </InputAddon>
        <CurrencyInput
          aria-label="Verba de sell-in"
          name="verbaDeSellIn"
          decimalSeparator=","
          thousandSeparator="."
          precision={DECIMAIS_DA_UNIDADE[unidade]}
          placeholder={PLACEHOLDER_DA_VERBA}
          allowNull
          // O sinal LIGADO é o que torna a recusa por valor negativo alcançável: desligado, o
          // componente de moeda descarta o menos e digitar -0,10 grava verba de 0,10, sem erro
          // na tela. Quem impede o valor recusado de ir ao servidor é `verbaParaGravarDaEdicao`.
          allowNegative
          value={valorNaUnidade}
          disabled={campo.desabilitado}
          readOnly={campo.somenteLeitura}
          onChangeEvent={aoDigitar}
          onBlur={aoSairDoCampo}
        />
        <InputAddon>
          <button
            type="button"
            title={TITULO_DO_BOTAO_DE_UNIDADE}
            aria-label={TITULO_DO_BOTAO_DE_UNIDADE}
            className={styles['botao-unidade']}
            disabled={campo.desabilitado || campo.somenteLeitura}
            onClick={() => onChangeUnidade(unidade === 'REAIS' ? 'PONTOS' : 'REAIS')}
          >
            {simbolo}
          </button>
        </InputAddon>
      </InputGroup>

      {campo.selo || campo.linha1.length > 0 ? (
        <p className={styles['verba-linha']}>
          {campo.selo ? <span className={styles[`selo-${campo.moldura === 'ERRO' ? 'erro' : campo.selo.replace(' ', '-')}`]}>{campo.selo}</span> : null}
          {campo.linha1.map(segmento => (
            <span key={segmento.texto} className={styles[`tom-${segmento.tom}`]}>
              {segmento.texto}
            </span>
          ))}
        </p>
      ) : null}

      {campo.linha2.length > 0 ? (
        <p className={styles['verba-linha']}>
          {campo.linha2.map(segmento => (
            <span key={segmento.texto} className={styles[`tom-${segmento.tom}`]}>
              {segmento.texto}
            </span>
          ))}
        </p>
      ) : null}
    </div>
  );
};
